import type { BlogPost } from "./blog-posts.ts";

// Posts escritos em 10/10/2026 e revisados pela Clara. Novos textos podem entrar
// aqui com `draft: true` enquanto estiverem em revisão (só aparecem em `npm run dev`).
export const draftPosts: BlogPost[] = [
  // ─── RASCUNHO: produtos brasileiros nos mercados ─────────────────────────
  {
    slug: "produtos-brasileiros-no-coles-woolworths-e-aldi",
    title: "Produtos brasileiros no Coles, Woolworths e Aldi: o que existe e o que substituir",
    seoTitle: "Produtos brasileiros no Coles, Woolworths e Aldi: guia completo",
    metaDescription:
      "Feijão, farofa, pão de queijo, açaí, tapioca: o que você acha nos mercados australianos, o que precisa de loja brasileira e quais substituições funcionam.",
    category: "guia-de-compras",
    categoryLabel: "Guia de compras",
    date: "2026-10-10",
    readTime: "9 min",
    excerpt:
      "A pergunta que toda brasileira faz na primeira semana: onde eu acho feijão, farofa e pão de queijo aqui? A resposta é melhor do que parece.",
    relatedSlugs: [
      "o-que-comprar-no-coles-e-woolworths-para-comer-melhor",
      "como-montar-marmitas-na-australia-sem-passar-horas-cozinhando",
      "como-bater-proteina-na-australia-com-opcoes-acessiveis",
    ],
    content: [
      {
        type: "p",
        text: "Toda brasileira que chega na Austrália passa por isso: entra no Woolworths procurando feijão carioca, farinha de mandioca e requeijão e sai com a sensação de que vai ter que mudar tudo. Não vai. Boa parte do que você comia no Brasil existe aqui, só com outro nome, em outro corredor ou em outra loja.",
      },
      {
        type: "p",
        text: "Este guia é a lista que eu passo para minhas pacientes: o que você encontra nos três grandes mercados (Coles, Woolworths e Aldi), o que só aparece em loja brasileira ou asiática e quais trocas funcionam bem na prática, sem perder o sabor de casa.",
      },
      { type: "h2", text: "O que você encontra nos mercados comuns" },
      {
        type: "list",
        items: [
          "Feijão preto: em lata (black beans) nos três mercados, no corredor de enlatados. Em grão seco, procure na seção de legumes secos ou na ala de produtos internacionais.",
          "Açaí: polpa congelada em pacotinhos no freezer do Woolworths e do Coles, perto das frutas congeladas. Prefira a versão sem açúcar e adoce com banana.",
          "Tapioca: a goma vendida como tapioca flour ou tapioca starch, no corredor de produtos sem glúten ou de farinhas. Hidrata do mesmo jeito que no Brasil.",
          "Polenta e fubá: polenta (fina ou instantânea) no corredor de massas e arroz. Serve para angu, bolo de fubá e polenta cremosa.",
          "Leite condensado e creme de leite: condensed milk e thickened cream, no corredor de confeitaria e na geladeira. O creme de leite daqui é mais gordo; use um pouco menos.",
          "Coco: leite de coco, água de coco e coco ralado seco estão em qualquer um dos três mercados.",
          "Carne moída, coxa de frango e ovos: a base da comida brasileira do dia a dia está sempre lá, e costuma ser mais barata no Aldi.",
          "Pão de queijo: aparece congelado em algumas lojas, geralmente como Brazilian cheese bread, no freezer de pães. Nem toda unidade tem.",
        ],
      },
      { type: "h2", text: "O que você vai achar em loja brasileira ou asiática" },
      {
        type: "p",
        text: "Brisbane, Sydney, Melbourne e Gold Coast têm mercadinhos brasileiros, e os asian grocers quebram um galho enorme para raízes e farinhas. É neles que ficam os itens que os grandes mercados não têm ou têm só de vez em quando.",
      },
      {
        type: "list",
        items: [
          "Farinha de mandioca e farofa pronta: praticamente só em loja brasileira. Compre em quantidade e guarde fechado, dura bastante.",
          "Feijão carioca de verdade: loja brasileira. No mercado comum, as trocas mais próximas são o borlotti (textura parecida) e o pinto bean, quando aparece.",
          "Mandioca (aipim): congelada nos asian grocers como cassava, às vezes também no Woolworths. Cozinha igual.",
          "Goiabada, doce de leite e paçoca: loja brasileira. O dulce de leche argentino, que aparece em alguns Coles, substitui bem o doce de leite.",
          "Guaraná, café brasileiro e erva-mate: loja brasileira ou online.",
          "Queijo coalho e queijo minas: raros. O haloumi grelha parecido com o coalho; para o minas, a ricota fresca é a troca mais usada.",
        ],
      },
      { type: "h2", text: "Trocas que funcionam (e que eu uso nos planos)" },
      {
        type: "p",
        text: "Quando o produto original não está disponível, a troca certa mantém a função daquele alimento na refeição: a proteína continua proteína, a fibra continua fibra. Essas são as que mais aprovam:",
      },
      {
        type: "list",
        items: [
          "Requeijão: cream cheese light ou ricota batida com um fio de leite. Para passar no pão, o cottage também resolve.",
          "Pão francês: bread rolls das padarias dos mercados, de manhã, saem quentinhos. O sourdough é a opção com mais fibra.",
          "Farofa: se não tiver farinha de mandioca, uma farofa de aveia em flocos tostada na frigideira com cebola e manteiga surpreende.",
          "Linguiça toscana: as sausages de porco com poucos temperos. Leia o rótulo e prefira as com mais carne e menos recheio.",
          "Couve: kale ou cavolo nero, refogados do mesmo jeito. O silverbeet também serve.",
          "Pão de queijo caseiro: tapioca flour, queijo tasty ralado, ovo e leite. Rende mais barato que o congelado.",
        ],
      },
      { type: "h2", text: "Como montar a despensa sem gastar demais" },
      {
        type: "p",
        text: "O segredo é separar os itens em duas listas: a semanal, nos grandes mercados (frescos, proteínas, básicos) e a mensal, na loja brasileira (farinha, feijão carioca, café). Aproveite os weekly specials do Coles e do Woolworths para os itens de despensa e compre no Aldi o que você usa em volume.",
      },
      {
        type: "p",
        text: "Comer com sabor de Brasil na Austrália não é questão de achar os mesmos rótulos, e sim de saber onde cada coisa está e o que troca com o quê. É exatamente isso que entra no plano alimentar que eu monto para cada paciente: comida de verdade, com o que existe aqui.",
      },
      {
        type: "internal-link",
        linkText: "Veja o guia completo do mercado australiano, corredor por corredor",
        linkHref: "/mercado-australiano-o-que-comprar",
      },
      {
        type: "internal-link",
        linkText: "Agende uma consulta e monte um plano com os produtos que você encontra aqui",
        linkHref: "/consulta-inicial-gratuita",
      },
      {
        type: "blog-link",
        linkText: "Leia também: o que comprar no Coles e Woolworths para comer melhor",
        linkHref: "/blog/o-que-comprar-no-coles-e-woolworths-para-comer-melhor",
      },
    ],
  },

  // ─── RASCUNHO: nutricionista x dietitian ─────────────────────────────────
  {
    slug: "nutricionista-ou-dietitian-na-australia-qual-a-diferenca",
    title: "Nutricionista ou dietitian na Austrália: qual a diferença e quem procurar",
    seoTitle: "Nutricionista ou dietitian na Austrália: qual a diferença?",
    metaDescription:
      "Na Austrália, nutricionista e dietitian não são a mesma coisa. Entenda o que cada um faz, como funciona o Medicare e quem procurar para cada situação.",
    category: "habitos-e-saude",
    categoryLabel: "Hábitos e saúde",
    date: "2026-10-10",
    readTime: "7 min",
    excerpt:
      "No Brasil é tudo nutricionista. Aqui, as palavras nutritionist e dietitian querem dizer coisas diferentes, e isso muda quem você deve procurar.",
    relatedSlugs: [
      "quanto-custa-nutricionista-na-australia",
      "por-que-morar-fora-bagunca-alimentacao-e-como-voltar-ao-eixo",
      "guia-pratico-habitos-sono-fome-energia-para-imigrantes",
    ],
    content: [
      {
        type: "p",
        text: "No Brasil, quem cuida da sua alimentação é a nutricionista, ponto. A profissão é regulamentada, tem conselho (CRN) e todo mundo sabe o que esperar. Na Austrália, a história é outra: existem dois títulos, nutritionist e dietitian, e eles não são sinônimos.",
      },
      {
        type: "p",
        text: "Entender essa diferença evita frustração, gasto desnecessário e, principalmente, ajuda você a procurar a pessoa certa para cada situação.",
      },
      { type: "h2", text: "O que é um dietitian na Austrália" },
      {
        type: "p",
        text: "Dietitian é o profissional com formação universitária específica em dietética e credenciamento pela Dietitians Australia, geralmente com o título APD (Accredited Practising Dietitian). É esse credenciamento que permite trabalhar em hospitais, atender casos clínicos complexos e receber encaminhamentos com reembolso do Medicare.",
      },
      {
        type: "p",
        text: "Quando o GP monta um plano de doença crônica (o Chronic Disease Management plan), o paciente pode ter algumas consultas por ano com profissionais de saúde aliados, incluindo dietitians, com parte do valor reembolsada. As regras e os valores mudam de tempos em tempos, então vale confirmar com o seu GP.",
      },
      { type: "h2", text: "O que é um nutritionist na Austrália" },
      {
        type: "p",
        text: "Nutritionist, por outro lado, não é um título regulado por lei na Austrália. Qualquer pessoa pode se chamar assim, de quem fez um curso de fim de semana a quem tem graduação completa em Nutrição. Existe um registro voluntário, da Nutrition Society of Australia, mas ele não é obrigatório.",
      },
      {
        type: "p",
        text: "Na prática, isso significa que o cuidado de quem procura um nutritionist é olhar a formação da pessoa: onde estudou, por quanto tempo e com o que trabalha. Um diploma de Nutrição de uma universidade brasileira, por exemplo, é uma graduação completa de quatro anos com estágio supervisionado.",
      },
      { type: "h2", text: "E as nutricionistas brasileiras que atendem aqui?" },
      {
        type: "p",
        text: "Muitas nutricionistas formadas no Brasil atendem a comunidade brasileira na Austrália, em português, como nutritionists. É o meu caso. O foco desse tipo de atendimento é o que mais faz falta para quem imigra: plano alimentar com os produtos daqui, acompanhamento da rotina, ajuste de hábitos e constância, tudo na sua língua.",
      },
      {
        type: "p",
        text: "O que esse atendimento não substitui é o tratamento clínico de uma condição diagnosticada que exija dietitian ou médico, nem o reembolso do Medicare, que é exclusivo dos APDs. Uma profissional séria vai te dizer isso na primeira conversa e, se for o caso, te orientar a procurar o GP.",
      },
      { type: "h2", text: "Quem procurar em cada situação" },
      {
        type: "list",
        items: [
          "Diagnóstico de doença (diabetes, doença renal, alergia grave, transtorno alimentar): comece pelo GP e peça encaminhamento para dietitian APD.",
          "Emagrecer, ganhar massa, ter mais energia, organizar a alimentação com a rotina australiana: nutricionista ou dietitian, o que importa é a formação e o método.",
          "Dificuldade de explicar sintomas e hábitos em inglês: um atendimento em português costuma render mais, porque você fala sem filtro.",
          "Quer usar o reembolso do Medicare ou do plano de saúde: confirme antes se o profissional é APD e se o seu plano cobre.",
        ],
      },
      { type: "h2", text: "Como checar a formação de quem vai te atender" },
      {
        type: "list",
        items: [
          "Pergunte onde a pessoa se formou e há quanto tempo atua. Resposta vaga é sinal de alerta.",
          "Para dietitians, procure o nome no diretório da Dietitians Australia.",
          "Desconfie de promessas de resultado rápido, de dieta igual para todo mundo e de venda de suplemento como parte do pacote.",
          "Prefira quem oferece uma primeira conversa para entender o seu caso antes de fechar qualquer plano.",
        ],
      },
      {
        type: "p",
        text: "Nenhum dos dois títulos é melhor que o outro em tudo. Eles servem para coisas diferentes. Saber disso coloca você no controle da escolha, em vez de descobrir a diferença depois de pagar a consulta.",
      },
      {
        type: "internal-link",
        linkText: "Conheça a minha formação e como funciona o atendimento em português",
        linkHref: "/sobre",
      },
      {
        type: "internal-link",
        linkText: "Agende uma consulta e tire suas dúvidas sobre o atendimento",
        linkHref: "/consulta-inicial-gratuita",
      },
    ],
  },

  // ─── RASCUNHO: quanto custa ──────────────────────────────────────────────
  {
    slug: "quanto-custa-nutricionista-na-australia",
    title: "Quanto custa uma nutricionista na Austrália (e o que está incluso)",
    seoTitle: "Quanto custa uma nutricionista na Austrália em 2026",
    metaDescription:
      "Faixas de preço de consulta com nutricionista e dietitian na Austrália, o que o Medicare e o plano de saúde cobrem e o que olhar além do valor da consulta.",
    category: "habitos-e-saude",
    categoryLabel: "Hábitos e saúde",
    date: "2026-10-10",
    readTime: "7 min",
    excerpt:
      "Consulta avulsa, pacote mensal, reembolso do Medicare: os valores variam muito. Veja as faixas mais comuns e o que realmente importa na hora de comparar.",
    relatedSlugs: [
      "nutricionista-ou-dietitian-na-australia-qual-a-diferenca",
      "emagrecimento-sem-dieta-maluca-como-ter-constancia",
      "como-organizar-alimentacao-na-australia-com-rotina-corrida",
    ],
    content: [
      {
        type: "p",
        text: "Quem pesquisa quanto custa uma nutricionista na Austrália encontra de tudo: consulta de 80 dólares, pacote de 600, programa online de 40 por mês. A variação é real, e ela acontece porque os serviços são diferentes entre si. Este texto organiza as faixas mais comuns em 2026 e o que você deve comparar além do preço.",
      },
      { type: "h2", text: "Faixas de preço que você vai encontrar" },
      {
        type: "list",
        items: [
          "Consulta inicial com dietitian em clínica: costuma ficar entre 120 e 200 dólares, com duração de 45 a 60 minutos.",
          "Consultas de retorno: geralmente entre 70 e 120 dólares, mais curtas.",
          "Nutritionists em consultório ou online: faixa parecida ou um pouco menor, com grande variação conforme a formação e a experiência.",
          "Pacotes e acompanhamentos mensais: vão de valores modestos, quando é só um plano em PDF, até programas completos com consultas, suporte e plataforma.",
          "Programas 100% automatizados por aplicativo: os mais baratos, mas sem acompanhamento individual.",
        ],
      },
      {
        type: "p",
        text: "Esses valores são referências de mercado nas capitais e podem mudar por cidade, por profissional e com o tempo. Use como ponto de partida, não como tabela.",
      },
      { type: "h2", text: "Medicare e plano de saúde: o que cobre" },
      {
        type: "p",
        text: "O Medicare só reembolsa consultas com dietitians credenciados (APD) e apenas dentro de um plano de doença crônica montado pelo GP, com um número limitado de consultas por ano. O reembolso cobre parte do valor, não o total. Para emagrecimento sem diagnóstico associado, normalmente não há cobertura.",
      },
      {
        type: "p",
        text: "Planos de saúde privados com extras podem reembolsar consultas de dietitian e, em alguns casos, de nutritionist, dependendo da operadora e do nível do plano. Vale ligar para o seu plano antes de agendar e perguntar exatamente o que é coberto.",
      },
      { type: "h2", text: "O que olhar além do valor da consulta" },
      {
        type: "p",
        text: "Comparar só o preço por consulta engana. Uma consulta avulsa barata que termina com um plano genérico sai caro quando você desiste na segunda semana. Pergunte o que acontece entre as consultas:",
      },
      {
        type: "list",
        items: [
          "Tem suporte entre as consultas (WhatsApp, app, mensagens) ou você fica sozinha até o retorno?",
          "O plano é feito com os produtos que você encontra aqui e cabe no seu orçamento semanal de mercado?",
          "Quantas consultas e ajustes estão incluídos no período?",
          "Existe material de apoio: receitas, listas de compras, guias?",
          "O atendimento é na sua língua? Para muita gente, isso define se o acompanhamento funciona ou não.",
        ],
      },
      { type: "h2", text: "Como funciona no meu atendimento" },
      {
        type: "p",
        text: "O primeiro passo é uma consulta inicial de 30 minutos, sem custo. Nela, entendo sua rotina e seus objetivos e apresento as opções de acompanhamento com os valores, para você decidir com tudo na mesa. O acompanhamento inclui plano alimentar personalizado com produtos do Coles, Woolworths e Aldi, acesso à plataforma, contato direto comigo por WhatsApp e ajustes conforme a rotina muda.",
      },
      {
        type: "p",
        text: "Sugiro um período mínimo de três meses porque é o tempo em que a mudança de hábito se consolida, mas não existe multa nem burocracia para parar. O custo que mais pesa, na minha experiência, é o de recomeçar do zero a cada tentativa.",
      },
      {
        type: "internal-link",
        linkText: "Veja o que está incluído no acompanhamento nutricional",
        linkHref: "/acompanhamento-nutricional-australia",
      },
      {
        type: "internal-link",
        linkText: "Agende uma consulta e conheça as opções de acompanhamento e os valores",
        linkHref: "/consulta-inicial-gratuita",
      },
    ],
  },

  // ─── RASCUNHO: whey e suplementos ────────────────────────────────────────
  {
    slug: "whey-e-suplementos-na-australia-o-que-comprar",
    title: "Whey e suplementos na Austrália: o que vale a pena e onde comprar",
    seoTitle: "Whey e suplementos na Austrália: guia de compra",
    metaDescription:
      "Onde comprar whey na Austrália, como comparar preço por dose, o que olhar no rótulo e quais suplementos realmente fazem diferença para quem treina.",
    category: "habitos-e-saude",
    categoryLabel: "Hábitos e saúde",
    date: "2026-10-10",
    readTime: "8 min",
    excerpt:
      "Chemist Warehouse, mercado, loja de suplemento ou online? E qual whey escolher entre tantas marcas? Um guia direto, sem promessa milagrosa.",
    relatedSlugs: [
      "como-bater-proteina-na-australia-com-opcoes-acessiveis",
      "emagrecimento-sem-dieta-maluca-como-ter-constancia",
      "guia-pratico-habitos-sono-fome-energia-para-imigrantes",
    ],
    content: [
      {
        type: "p",
        text: "Quem treina e chega na Austrália logo pergunta: onde compro whey e quanto custa? A boa notícia é que suplemento aqui é fácil de achar e, comparando por dose, costuma sair mais em conta do que no Brasil. A má notícia é que a prateleira é enorme e cheia de promessa.",
      },
      {
        type: "p",
        text: "Antes de tudo: suplemento complementa a alimentação, não a substitui. Se a sua base de proteína, fibra e vegetais não está resolvida, nenhum pote resolve. Dito isso, vamos ao que funciona.",
      },
      { type: "h2", text: "Onde comprar" },
      {
        type: "list",
        items: [
          "Chemist Warehouse: a opção mais prática. Tem whey, creatina e vitaminas de várias marcas, com promoções frequentes. Compare o preço por quilo entre os potes.",
          "Coles e Woolworths: variedade pequena, no corredor de saúde, com preço geralmente maior por dose. Serve para emergência.",
          "Lojas de suplemento: mais variedade e atendimento, com preço variando bastante. Vale quando você já sabe o que quer.",
          "Online: as lojas australianas de suplemento costumam ter os melhores preços por quilo, especialmente em potes grandes. Confira o frete e a data de validade.",
        ],
      },
      { type: "h2", text: "Como escolher um whey sem cair em propaganda" },
      {
        type: "p",
        text: "Ignore o nome fantasia e vá para a tabela nutricional. O que importa é quanto de proteína vem em cada dose e quanto você paga por essa dose.",
      },
      {
        type: "list",
        items: [
          "Proteína por dose: procure algo em torno de 20 a 25 gramas por scoop. Menos que isso, o pote rende menos do que parece.",
          "Preço por dose: divida o preço pelo número de doses do pote. É a única comparação justa entre marcas e tamanhos.",
          "Concentrado, isolado ou blend: o concentrado é o melhor custo-benefício para a maioria. O isolado faz sentido se você tem desconforto com lactose.",
          "Açúcar e aditivos: alguns sabores vêm carregados de açúcar ou adoçante. Baunilha e chocolate simples costumam ser os mais limpos.",
          "Proteína vegetal: ervilha e arroz combinados chegam perto do whey em qualidade. Boa opção para quem não consome laticínios.",
        ],
      },
      { type: "h2", text: "Suplementos que fazem diferença (e os que não fazem)" },
      {
        type: "p",
        text: "Creatina monohidratada é o suplemento com mais evidência para quem treina força, é barata e segura para a maioria das pessoas saudáveis. Whey ou proteína vegetal ajudam quando você não consegue bater a meta de proteína só com comida. Cafeína antes do treino funciona para muita gente, e o café resolve isso.",
      },
      {
        type: "p",
        text: "O que costuma ser dinheiro jogado fora: termogênicos e queimadores de gordura, detox, pacotes de vitaminas sem exame que mostre deficiência e qualquer coisa vendida com promessa de resultado em semanas. Na Austrália, os suplementos são regulados pela TGA; procure o código AUST L ou AUST R no rótulo, que indica que o produto é registrado no país.",
      },
      { type: "h2", text: "Quando vale conversar com uma nutricionista" },
      {
        type: "p",
        text: "Se você está em dúvida sobre quanto de proteína precisa, se faz sentido suplementar ou se está gastando com algo que não traz resultado, uma avaliação individual resolve isso em uma conversa. Eu olho a sua alimentação, o seu treino e a sua rotina antes de qualquer recomendação, e muitas vezes a resposta é ajustar a comida, não comprar mais um pote.",
      },
      {
        type: "blog-link",
        linkText: "Leia também: como bater a meta de proteína na Austrália com opções acessíveis",
        linkHref: "/blog/como-bater-proteina-na-australia-com-opcoes-acessiveis",
      },
      {
        type: "internal-link",
        linkText: "Agende uma consulta e descubra se você precisa mesmo suplementar",
        linkHref: "/consulta-inicial-gratuita",
      },
    ],
  },
];
